/**
 * Dark mode toggle (F-122) — a sun/moon icon button dropped into any topbar
 * (BoardScreen, Dashboard, Admin). Local view state only (core/theme.ts); no
 * CRDT. Labelled through `data-tip` (the app.css hover bubble) rather than
 * `title`, like every other icon-only topbar control.
 */
import { useEffect, useState } from "react";

import { effectiveTheme, isThemeForced, toggleTheme, type Theme } from "../core/theme.js";

const svgProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 16 16",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const iconSun = (
  <svg {...svgProps} aria-hidden="true">
    <circle cx="8" cy="8" r="3.2" />
    <path d="M8 1.5v1.6M8 12.9v1.6M14.5 8h-1.6M3.1 8H1.5M12.5 3.5l-1.1 1.1M4.6 11.4l-1.1 1.1M12.5 12.5l-1.1-1.1M4.6 4.6 3.5 3.5" />
  </svg>
);

const iconMoon = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M13.5 9.8A5.8 5.8 0 0 1 6.2 2.5a5.8 5.8 0 1 0 7.3 7.3z" />
  </svg>
);

export function ThemeToggle() {
  // A host that IMPOSES its theme (core/theme.ts `forceTheme`; none does
  // today) gets no toggle: one that could not change anything is worse than
  // none. The desktop app only sets a default, so its toggle shows.
  if (isThemeForced()) return null;
  return <ThemeToggleButton />;
}

function ThemeToggleButton() {
  // Mirrors the applied theme locally so the icon flips instantly on click and
  // stays correct if the OS preference changes underneath an unchanged choice
  // (core/theme.ts keeps listening) — polled on mount only, cheap and rare.
  const [theme, setThemeState] = useState<Theme>(() => effectiveTheme());

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setThemeState(effectiveTheme());
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return (
    <button
      type="button"
      className="btn btn-ghost"
      onClick={() => setThemeState(toggleTheme())}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      data-tip={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      aria-pressed={theme === "dark"}
    >
      {theme === "dark" ? iconMoon : iconSun}
    </button>
  );
}
